import { useMutation } from '@base-app/web2'
import { memo, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CREATE_PODCAST, UPDATE_PODCAST } from '../../../graphql/podcasts/mutations'
import { PODCAST_QUERY } from '../../../graphql/podcasts/queries'
import type { Podcast } from '../../../graphql/podcasts/types'
import { useRecordById } from '../../../hooks/useRecordById'
import { useToast } from '../../../hooks/useToast'
import { prependToLists } from '../../../lib/cacheLists'
import { isWebUrl } from '../../../services/url-validation'
import { Button, FormActions } from '../../atoms'
import { FormField, FormLoadState, TextareaField } from '../../molecules'

interface PodcastFormFieldsProps {
  podcast?: Podcast
  clientId: string
  appId: string
  onSuccess: () => void
  onClose: () => void
  // Renders every field as its pulsing placeholder while the record loads
  skeleton?: boolean
}

export const PodcastFormFields = memo(
  ({ podcast, clientId, appId, onSuccess, onClose, skeleton }: PodcastFormFieldsProps) => {
    const { t } = useTranslation('admin-mobifull')
    const { showToast } = useToast()
    const isEdit = !!podcast
    const [name, setName] = useState(podcast?.name ?? '')
    const [embeddedLink, setEmbeddedLink] = useState(podcast?.embedded_link ?? '')
    const [description, setDescription] = useState(podcast?.description ?? '')
    const [observations, setObservations] = useState(podcast?.observations ?? '')
    const [errors, setErrors] = useState<{ name?: string; embeddedLink?: string }>({})

    const makeOptimisticPodcast = useCallback(
      (id: string): Podcast => ({
        __typename: 'Podcast',
        id,
        client_id: clientId,
        application_id: appId,
        name,
        embedded_link: embeddedLink || null,
        description: description || null,
        observations: observations || null,
        thumbnail: null,
        synchronized_at: null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }),
      [clientId, appId, name, embeddedLink, description, observations],
    )

    const [createPodcast, { loading: creating }] = useMutation(CREATE_PODCAST, {
      optimisticResponse: { createPodcast: makeOptimisticPodcast(`temp-${Date.now()}`) },
      update(cache, result) {
        const newPodcast = result.data?.createPodcast
        if (!newPodcast) {
          return
        }
        prependToLists(cache, 'podcasts', newPodcast, { clientId, applicationId: appId })
      },
      onError(err) {
        showToast(err.message, 'error')
      },
      onCompleted() {
        onSuccess()
      },
    })

    const [updatePodcast, { loading: updating }] = useMutation(UPDATE_PODCAST, {
      optimisticResponse: { updatePodcast: makeOptimisticPodcast(podcast?.id ?? '') },
      onError(err) {
        showToast(err.message, 'error')
      },
      onCompleted() {
        onSuccess()
      },
    })

    const loading = creating || updating

    const validate = useCallback(() => {
      const errs: { name?: string; embeddedLink?: string } = {}
      if (!name.trim()) {
        errs.name = t('common.form.nameRequired')
      }
      if (!isWebUrl(embeddedLink.trim())) {
        errs.embeddedLink = t('common.form.httpsUrlInvalid')
      }
      setErrors(errs)
      return Object.keys(errs).length === 0
    }, [name, embeddedLink, t])

    const handleSubmit = useCallback(
      (e: React.FormEvent) => {
        e.preventDefault()
        if (!validate()) {
          return
        }
        const vars = {
          name,
          embedded_link: embeddedLink || null,
          description: description || null,
          observations: observations || null,
        }
        if (isEdit) {
          updatePodcast({ variables: { id: podcast.id, ...vars } })
        } else {
          createPodcast({ variables: { client_id: clientId, application_id: appId, ...vars } })
        }
      },
      [
        validate,
        isEdit,
        podcast?.id,
        name,
        embeddedLink,
        description,
        observations,
        clientId,
        appId,
        updatePodcast,
        createPodcast,
      ],
    )

    return (
      <form onSubmit={handleSubmit} noValidate aria-busy={skeleton}>
        {skeleton ? (
          <p className="sr-only" role="status">
            {t('common.loading')}
          </p>
        ) : null}
        <FormField
          skeleton={skeleton}
          id="podcast-name"
          label={t('podcasts.form.nameLabel')}
          name="name"
          value={name}
          onChange={e => setName(e.target.value)}
          error={errors.name}
          required
        />
        <div className="form-group">
          <FormField
            skeleton={skeleton}
            id="podcast-embedded-link"
            label={t('podcasts.form.embeddedLinkLabel')}
            name="embedded_link"
            value={embeddedLink}
            onChange={e => setEmbeddedLink(e.target.value)}
            error={errors.embeddedLink}
            placeholder="https://"
          />
          <Button
            skeleton={skeleton}
            type="button"
            variant="secondary"
            disabled={!embeddedLink || !isWebUrl(embeddedLink.trim())}
            onClick={() => window.open(embeddedLink, '_blank', 'noopener')}>
            {t('podcasts.form.previewButton')}
          </Button>
        </div>
        <TextareaField
          skeleton={skeleton}
          id="podcast-description"
          label={t('podcasts.form.descriptionLabel')}
          name="description"
          value={description ?? ''}
          onChange={e => setDescription(e.target.value)}
        />
        <TextareaField
          skeleton={skeleton}
          id="podcast-observations"
          label={t('podcasts.form.observationsLabel')}
          name="observations"
          value={observations ?? ''}
          onChange={e => setObservations(e.target.value)}
        />
        <FormActions>
          <Button type="button" variant="secondary" onClick={onClose} disabled={loading}>
            {t('common.actions.cancel')}
          </Button>
          <Button skeleton={skeleton} type="submit" disabled={loading}>
            {loading ? t('podcasts.form.saving') : t('podcasts.form.save')}
          </Button>
        </FormActions>
      </form>
    )
  },
)

PodcastFormFields.displayName = 'PodcastFormFields'

interface PodcastFormProps extends Omit<PodcastFormFieldsProps, 'podcast'> {
  // The record to edit, by id; omit it to create a new one
  id?: string
}

// Loads the record by id (from the cache when the list has it, otherwise from the API)
export const PodcastForm = memo(({ id, ...props }: PodcastFormProps) => {
  const { record, status } = useRecordById(PODCAST_QUERY, id, data => data.podcast)
  if (status === 'loading') {
    return <PodcastFormFields skeleton {...props} />
  }
  if (status !== 'new' && status !== 'ready') {
    return <FormLoadState status={status} onClose={props.onClose} />
  }
  return <PodcastFormFields key={id ?? 'new'} podcast={record} {...props} />
})

PodcastForm.displayName = 'PodcastForm'
